import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Mail, Send, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RequestMoreDialog
 *
 * Lets the promoter preview the follow-up email before sending it, attach an
 * optional personal note, and send only once per contributor. If a follow-up
 * was already sent, shows a read-only preview and disables the send button.
 *
 * Props:
 *   - open, onOpenChange
 *   - bookId, contributorId, contributorName
 *   - stageLabel (optional) — adds stage context to the preview
 *   - onSent (fn) — called after a successful send
 */
export default function RequestMoreDialog({ open, onOpenChange, bookId, contributorId, contributorName, stageLabel, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [alreadySent, setAlreadySent] = useState(false);

  useEffect(() => {
    if (!open || !contributorId) return;
    setLoading(true);
    setMessage('');
    axios.post(
      `${API_URL}/api/books/${bookId}/contributors/${contributorId}/request-more`,
      { stage_label: stageLabel || '', preview_only: true },
      { withCredentials: true }
    )
      .then((res) => {
        setPreview(res.data);
        setAlreadySent(!!res.data.already_sent);
      })
      .catch((err) => toast.error(err.response?.data?.detail || 'No se pudo cargar la vista previa.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contributorId]);

  // Re-fetch preview when the promoter types a personal message so the
  // preview reflects what will be sent.
  useEffect(() => {
    if (!open || !contributorId || loading) return;
    const timer = setTimeout(() => {
      axios.post(
        `${API_URL}/api/books/${bookId}/contributors/${contributorId}/request-more`,
        { stage_label: stageLabel || '', message, preview_only: true },
        { withCredentials: true }
      ).then((res) => setPreview(res.data)).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  const send = async () => {
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/contributors/${contributorId}/request-more`,
        { stage_label: stageLabel || '', message },
        { withCredentials: true }
      );
      toast.success(`Email enviado a ${contributorName}.`);
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="request-more-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Pedir más contenido a {contributorName}
          </DialogTitle>
          <DialogDescription>
            {alreadySent
              ? 'Ya has pedido más contenido a esta persona. No se puede volver a enviar para no agobiarla.'
              : 'Este es el email que se enviará. Puedes añadir una nota personal si quieres.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 px-1">
            {alreadySent && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  Se envió el {new Date(preview?.already_sent_at).toLocaleString('es-ES')}. Para volver a cambiar el texto plantilla acude a <strong>Administración → Plantillas de notificación → "Pedir más contenido a colaborador"</strong>.
                </p>
              </div>
            )}
            {!alreadySent && (
              <div>
                <Label className="input-label">Nota personal (opcional)</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ej: Me acordé del verano en el que estuvimos en Cádiz juntos, ¿te acuerdas de aquello?"
                  rows={3}
                  className="text-sm"
                  data-testid="request-more-message"
                />
              </div>
            )}
            <div className="rounded-lg border bg-muted/10 overflow-hidden">
              <div className="px-4 py-2 border-b bg-white/60 text-xs text-muted-foreground flex items-center justify-between gap-2">
                <span>Para: <strong className="text-foreground">{preview?.to_email}</strong></span>
              </div>
              <div className="px-4 py-3 border-b bg-white/60">
                <p className="text-xs text-muted-foreground">Asunto</p>
                <p className="text-sm font-medium">{preview?.subject}</p>
              </div>
              <div className="px-4 py-3 bg-white/70">
                <p className="text-xs text-muted-foreground mb-1">Cuerpo del email</p>
                <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed text-foreground">
                  {preview?.body_text}
                </pre>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 pt-3 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {alreadySent ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!alreadySent && (
            <Button
              onClick={send}
              disabled={sending || loading}
              className="gap-2"
              data-testid="send-request-more-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar email
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
